import React from "react";
import api from "@/lib/api";
import { useList } from "@/lib/hooks";
import { Button } from "@/components/ui/button";
import { DownloadSimple, MapPin, Lightning } from "@phosphor-icons/react";
import { toast } from "sonner";

const EVENT = { created: "Link created", sms_sent: "Text sent", sms_failed: "Text failed", opened: "Link opened", accepted: "Driver accepted",
  paused: "Driver closed or left the page", resumed: "Sharing resumed", permission_denied: "Location permission denied", nudge_sent: "Resume reminder texted", nudge_failed: "Reminder text failed" };
const fmt = (iso) => new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const mins = (a, b) => Math.round((new Date(b) - new Date(a)) / 60000);

// Groups consecutive pings at the same address into one stop/segment entry, merged with lifecycle events.
function buildTimeline(points, events) {
  const segs = [];
  for (const p of points) {
    const key = p.address || `${p.lat.toFixed(3)},${p.lng.toFixed(3)}`;
    const cur = segs[segs.length - 1];
    if (cur && cur.key === key) { cur.to = p.at; cur.n += 1; cur.maxSpeed = Math.max(cur.maxSpeed, p.speed || 0); }
    else segs.push({ kind: "loc", key, address: p.address, lat: p.lat, lng: p.lng, from: p.at, to: p.at, n: 1, maxSpeed: p.speed || 0 });
  }
  return [...segs.map((s) => ({ ...s, at: s.from })), ...events.map((e) => ({ kind: "ev", ...e }))].sort((a, b) => (a.at < b.at ? 1 : -1));
}

export function TrackingLogs({ id, name }) {
  const { data } = useList(["tracking", id, "logs"], `/tracking/${id}/logs`, { refetchInterval: 20000 });
  const exportCsv = async () => {
    try {
      const { data: blob } = await api.get(`/tracking/${id}/logs`, { params: { format: "csv" }, responseType: "blob" });
      const url = URL.createObjectURL(blob);
      Object.assign(document.createElement("a"), { href: url, download: `tracking-${name}.csv` }).click();
      setTimeout(() => URL.revokeObjectURL(url), 3000);
    } catch { toast.error("Could not export the logs"); }
  };
  const rows = data ? buildTimeline(data.points, data.events) : [];
  return (
    <div className="border-t border-slate-200" data-testid="tracking-logs">
      <div className="flex items-center justify-between px-5 py-3">
        <p className="font-heading font-bold text-slate-900">Tracking Log <span className="text-xs font-normal text-slate-400">{data ? `${data.points.length} locations` : ""}</span></p>
        <Button size="sm" variant="outline" onClick={exportCsv} className="border-slate-300 bg-white" data-testid="tracking-logs-export"><DownloadSimple size={15} className="mr-1" />Export CSV</Button>
      </div>
      <ol className="max-h-[320px] overflow-y-auto px-5 pb-4 space-y-2">
        {rows.length === 0 && <li className="text-sm text-slate-400">No activity yet.</li>}
        {rows.map((r, i) => r.kind === "ev" ? (
          <li key={`e${i}`} className="flex gap-3 text-sm" data-testid="tracking-log-event"><Lightning size={16} className="text-amber-500 mt-0.5 shrink-0" />
            <span className="text-slate-500 w-32 shrink-0 font-mono text-xs mt-0.5">{fmt(r.at)}</span><span className="text-slate-700">{EVENT[r.type] || r.type}{r.detail ? ` · ${r.detail}` : ""}</span></li>
        ) : (
          <li key={`l${i}`} className="flex gap-3 text-sm" data-testid="tracking-log-location"><MapPin size={16} className="text-blue-600 mt-0.5 shrink-0" />
            <span className="text-slate-500 w-32 shrink-0 font-mono text-xs mt-0.5">{fmt(r.from)}</span>
            <span className="text-slate-800">{r.address || `${r.lat.toFixed(5)}, ${r.lng.toFixed(5)}`}
              <span className="block text-xs text-slate-400">{r.n > 1 ? `${mins(r.from, r.to)} min here · ${r.n} updates` : "passing"}{r.maxSpeed ? ` · up to ${Math.round(r.maxSpeed * 2.237)} mph` : ""}</span></span></li>
        ))}
      </ol>
    </div>
  );
}
